/**
 * BrandKits — manage reusable brand identities (logo, colors, fonts, tagline)
 * Route: /app/brand-kits
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Trash2, Star, Save, Upload, Image as ImageIcon, X, ArrowLeft, Layers, Check,
} from "lucide-react";

const DEFAULT_KIT = {
  name: "",
  company_name: "",
  tagline: "",
  primary: "#7C3AED",
  secondary: "#0B5CFF",
  accent: "#F59E0B",
  bg: "#FFFFFF",
  fg: "#0B0D17",
  font_heading: "Inter",
  font_body: "Inter",
  logo_data_url: null,
  is_default: false,
};

export default function BrandKits() {
  const nav = useNavigate();
  const [kits, setKits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // kit object or null
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/brand-kits");
      setKits(data.brand_kits || []);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing?.name?.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...editing };
      if (editing.id) {
        await api.put(`/brand-kits/${editing.id}`, payload);
        toast.success("Brand kit updated");
      } else {
        await api.post("/brand-kits", payload);
        toast.success("Brand kit created");
      }
      setEditing(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this brand kit?")) return;
    try {
      await api.delete(`/brand-kits/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const makeDefault = async (id) => {
    try {
      await api.post(`/brand-kits/${id}/default`);
      toast.success("Set as default");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <div className="space-y-6 vp-fade-up">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <button onClick={() => nav(-1)} className="p-2 rounded-lg hover:bg-[#F1F3F7]" data-testid="brand-back-btn"><ArrowLeft className="w-4 h-4" /></button>
          <div>
            <div className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-[#7C3AED]" />
              <h1 className="font-display font-black text-2xl">Brand Kits</h1>
            </div>
            <div className="text-sm text-[#4A4D57]">Reusable identity — apply to any presentation in one click.</div>
          </div>
        </div>
        <button onClick={() => setEditing({ ...DEFAULT_KIT })} className="vp-btn-primary text-sm py-2 px-3 inline-flex items-center gap-1.5" data-testid="new-brand-kit-btn">
          <Plus className="w-4 h-4" /> New brand kit
        </button>
      </div>

      {loading && <div className="text-sm text-[#747680]">Loading…</div>}

      {!loading && kits.length === 0 && (
        <div className="vp-card p-8 text-center">
          <Layers className="w-10 h-10 text-[#7C3AED] mx-auto mb-2" />
          <div className="font-display font-bold text-lg">No brand kits yet</div>
          <div className="text-sm text-[#4A4D57] mt-1">Create one so every presentation ships with your logo, colors and typography.</div>
          <button onClick={() => setEditing({ ...DEFAULT_KIT })} className="vp-btn-primary mt-4 text-sm py-2 px-4 inline-flex items-center gap-1.5" data-testid="empty-new-brand-kit-btn">
            <Plus className="w-4 h-4" /> Create your first brand kit
          </button>
        </div>
      )}

      {!loading && kits.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {kits.map((k) => (
            <div key={k.id} className="vp-card p-4 space-y-3" data-testid={`kit-card-${k.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-display font-bold text-lg truncate">{k.name}</div>
                  {k.company_name && <div className="text-xs text-[#747680] truncate">{k.company_name}</div>}
                </div>
                {k.is_default && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#F59E0B]/10 text-[#F59E0B] inline-flex items-center gap-1"><Star className="w-3 h-3" /> Default</span>
                )}
              </div>
              {/* palette swatches */}
              <div className="flex gap-1">
                {["primary","secondary","accent","bg","fg"].map((f) => (
                  <div key={f} className="flex-1 h-6 rounded" style={{ background: k[f] }} title={`${f}: ${k[f]}`} />
                ))}
              </div>
              {k.logo_data_url && (
                <div className="rounded-md p-3 flex items-center justify-center" style={{ background: k.bg }}>
                  <img src={k.logo_data_url} alt="logo" className="max-h-12 object-contain" />
                </div>
              )}
              <div className="flex items-center gap-2">
                <button onClick={() => setEditing(k)} className="vp-btn-ghost text-xs py-1.5 px-2 flex-1" data-testid={`edit-kit-${k.id}`}>Edit</button>
                {!k.is_default && (
                  <button onClick={() => makeDefault(k.id)} className="vp-btn-ghost text-xs py-1.5 px-2" title="Set default" data-testid={`default-kit-${k.id}`}><Star className="w-3.5 h-3.5" /></button>
                )}
                <button onClick={() => del(k.id)} className="vp-btn-ghost text-xs py-1.5 px-2 text-[#B00020]" data-testid={`del-kit-${k.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <KitEditor
          kit={editing}
          onChange={setEditing}
          onClose={() => setEditing(null)}
          onSave={save}
          saving={saving}
        />
      )}
    </div>
  );
}

function KitEditor({ kit, onChange, onClose, onSave, saving }) {
  const fileRef = useRef(null);
  const set = (patch) => onChange({ ...kit, ...patch });

  const pickFile = () => fileRef.current?.click();
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 800 * 1024) { toast.error("Logo must be <800KB"); return; }
    const reader = new FileReader();
    reader.onload = () => set({ logo_data_url: String(reader.result) });
    reader.readAsDataURL(f);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="vp-card bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="kit-editor">
        <div className="flex items-center justify-between p-4 border-b border-[#E4E6EC] sticky top-0 bg-white z-10">
          <div className="font-display font-bold text-lg">{kit.id ? "Edit brand kit" : "New brand kit"}</div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-[#F1F3F7]"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="vp-label">Kit name *</label>
              <input className="vp-input" value={kit.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Videoepreneur Main" data-testid="kit-name" />
            </div>
            <div>
              <label className="vp-label">Company name</label>
              <input className="vp-input" value={kit.company_name || ""} onChange={(e) => set({ company_name: e.target.value })} data-testid="kit-company" />
            </div>
          </div>
          <div>
            <label className="vp-label">Tagline</label>
            <input className="vp-input" value={kit.tagline || ""} onChange={(e) => set({ tagline: e.target.value })} placeholder="One-line pitch" data-testid="kit-tagline" />
          </div>
          <div>
            <label className="vp-label">Logo (PNG/JPG, &lt;800KB)</label>
            <div className="flex items-center gap-3">
              <button onClick={pickFile} className="vp-btn-ghost text-xs py-1.5 px-2 inline-flex items-center gap-1" data-testid="kit-upload-logo">
                <Upload className="w-3.5 h-3.5" /> Upload
              </button>
              <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" />
              {kit.logo_data_url ? (
                <div className="flex items-center gap-2">
                  <img src={kit.logo_data_url} alt="logo" className="h-8 max-w-[120px] object-contain rounded border border-[#E4E6EC] p-1" />
                  <button onClick={() => set({ logo_data_url: null })} className="text-xs text-[#B00020] hover:underline" data-testid="kit-clear-logo">Remove</button>
                </div>
              ) : <span className="text-xs text-[#747680]">No logo uploaded</span>}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {["primary","secondary","accent","bg","fg"].map((f) => (
              <ColorField key={f} label={f} value={kit[f]} onChange={(v) => set({ [f]: v })} testid={`kit-color-${f}`} />
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="vp-label">Heading font</label>
              <input className="vp-input" value={kit.font_heading || ""} onChange={(e) => set({ font_heading: e.target.value })} />
            </div>
            <div>
              <label className="vp-label">Body font</label>
              <input className="vp-input" value={kit.font_body || ""} onChange={(e) => set({ font_body: e.target.value })} />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!kit.is_default} onChange={(e) => set({ is_default: e.target.checked })} data-testid="kit-default" />
            Mark as default brand kit
          </label>
          {/* Live preview */}
          <div className="rounded-lg p-4 border border-[#E4E6EC]" style={{ background: kit.bg, color: kit.fg }}>
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-xs opacity-60">Preview</div>
                <div className="font-display font-black text-xl" style={{ color: kit.primary }}>{kit.name || "Your headline"}</div>
                {kit.tagline && <div className="text-xs opacity-70" style={{ color: kit.secondary }}>{kit.tagline}</div>}
              </div>
              {kit.logo_data_url && <img src={kit.logo_data_url} alt="logo" className="h-8 object-contain" />}
            </div>
            <div className="flex items-center gap-2 mt-3">
              <div className="w-2 h-2 rounded-full" style={{ background: kit.accent }} />
              <div className="text-sm">Bullet point rendered with accent color</div>
            </div>
          </div>
        </div>
        <div className="p-4 border-t border-[#E4E6EC] flex items-center justify-end gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="vp-btn-ghost text-sm py-2 px-3" data-testid="kit-cancel">Cancel</button>
          <button onClick={onSave} disabled={saving} className="vp-btn-primary text-sm py-2 px-3 inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="kit-save">
            {saving ? <Check className="w-4 h-4 animate-pulse" /> : <Save className="w-4 h-4" />} Save
          </button>
        </div>
      </div>
    </div>
  );
}

function ColorField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="vp-label capitalize">{label}</label>
      <div className="flex items-center gap-1">
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="w-9 h-9 rounded border border-[#E4E6EC] cursor-pointer" data-testid={testid} />
        <input className="vp-input flex-1 text-xs uppercase" value={value} onChange={(e) => onChange(e.target.value)} />
      </div>
    </div>
  );
}
